import { ChevronRight, ChevronDown, Building2, User, Users } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/utils'

import type { OrgDepartmentTreeNode } from '@/hooks/hr'

interface DepartmentTreeProps {
  data: OrgDepartmentTreeNode[]
  onSelect?: (department: OrgDepartmentTreeNode) => void
  selectedId?: string
}

export function DepartmentTree({ data, onSelect, selectedId }: DepartmentTreeProps) {
  return (
    <div className="space-y-1">
      {data.map((node) => (
        <DepartmentTreeItem
          key={node.id}
          node={node}
          level={0}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </div>
  )
}

interface DepartmentTreeItemProps {
  node: OrgDepartmentTreeNode
  level: number
  onSelect?: (department: OrgDepartmentTreeNode) => void
  selectedId?: string
}

function DepartmentTreeItem({ node, level, onSelect, selectedId }: DepartmentTreeItemProps) {
  const [isExpanded, setIsExpanded] = useState(level < 2) // 默认展开前两级
  const hasChildren = node.children.length > 0
  const isSelected = selectedId === node.id

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const handleSelect = () => {
    onSelect?.(node)
  }

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors',
          isSelected && 'bg-muted'
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleSelect}
      >
        {/* 展开/折叠按钮 */}
        <button
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded hover:bg-muted',
            !hasChildren && 'invisible'
          )}
          onClick={handleToggle}
        >
          {hasChildren && (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          )}
        </button>

        {/* 部门图标 */}
        <Building2 className="h-4 w-4 text-muted-foreground" />

        {/* 部门名称 */}
        <span className="flex-1 truncate text-sm font-medium">{node.name}</span>

        {/* 员工数量 */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="h-3 w-3" />
          <span>{node.employeeCount}</span>
        </div>

        {/* 负责人 */}
        {node.managerName && (
          <div className="flex items-center gap-1">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium">
              {node.managerName.slice(0, 1)}
            </div>
            <span className="text-xs text-muted-foreground">{node.managerName}</span>
          </div>
        )}
      </div>

      {/* 子部门 */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <DepartmentTreeItem
              key={child.id}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              selectedId={selectedId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * 部门详情面板
 */
interface DepartmentDetailProps {
  department: OrgDepartmentTreeNode
}

export function DepartmentDetail({ department }: DepartmentDetailProps) {
  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2">
          <Building2 className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">{department.name}</h3>
          <p className="text-sm text-muted-foreground">编码: {department.code}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>员工数量</span>
          </div>
          <p className="mt-1 text-2xl font-semibold">{department.employeeCount}</p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <span>部门负责人</span>
          </div>
          {department.managerName ? (
            <div className="mt-1 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                {department.managerName.slice(0, 1)}
              </div>
              <span className="font-medium">{department.managerName}</span>
            </div>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">未设置</p>
          )}
        </div>
      </div>

      {department.children.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">子部门</h4>
          <div className="space-y-1">
            {department.children.map((child) => (
              <div
                key={child.id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{child.name}</span>
                </div>
                <span className="text-xs text-muted-foreground">{child.employeeCount} 人</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
